/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import { baseComponentManager } from '../../../registry/Components';
import { ComponentManagerFn } from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { defaultValue } from './LOVGrid';

const LOVGridManager: ComponentManagerFn<'LOVGrid'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'LOVGrid'>(platform, 'LOVGrid'),
    type: 'LOVGrid',
    name: 'LOV Grid',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'CheckboxColumn',
          'DateColumn',
          'NumberColumn',
          'SelectColumn',
          'StringColumn',
          'RowSelectionColumn',
        ] as ItemType[]
      ).includes(childType),
    isPropertyVisible: () => false,
    defaultChildren: ['StringColumn'],
    defaultValue,
    props: {},
    category: 'Forms',
    icon: { iconName: 'table' },
    description: `**Overview**<br/>
The LOV Grid component serves as the structural container for the data table displayed within an **LOV Field** popup. It defines the columns and layout of the list that users see when they search for a record.
Use this component strictly as a child of an **LOV Field** to configure the visual representation of your data source.

**1. Column Configuration**<br/>
The LOV Grid does not have properties of its own; its appearance is determined entirely by the column components you nest inside it. You can add various column types to present data in the most readable format:
* **Supported Columns:**
    * **StringColumn:** For text data like Names or Descriptions.
    * **NumberColumn:** For IDs, Prices, or Quantities.
    * **DateColumn:** For Dates of Birth or Transaction Dates.
    * **CheckboxColumn:** For boolean flags (e.g., Active/Inactive).
    * **SelectColumn:** For mapped values.
    * **RowSelectionColumn:** To provide explicit row selection indicators.

**2. Zero Configuration**<br/>
This is a "pass-through" component designed for layout definition only.
* **No Properties:** It has no configurable properties (e.g., height, colors) on the component itself.
* **Inheritance:** The grid inherits its data source, dimensions, and behavior settings directly from the parent **LOV Field**.

**3. Best Use Cases**<br/>
* **Contextual Selection:** Defining a grid that shows "Customer Name," "City," and "Phone Number" so users can distinguish between two customers named "John Smith."
* **Data Preview:** Showing "Stock Quantity" and "Unit Price" columns in a Product selection popup to help sales agents pick the right item.
* **Reference Display:** Including a "Department Code" column alongside "Department Name" for clarity.`,
  };
};

export default LOVGridManager;
